import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, MessageSquare, CheckCircle, AlertTriangle, User } from 'lucide-react';
import { toast } from 'sonner';

interface EscalationTask {
  id: string;
  title?: string;
  description?: string | null;
  priority?: string;
  metadata?: Record<string, unknown> | null;
}

interface EscalationTaskCompletionModalProps {
  open: boolean;
  onClose: () => void;
  task: EscalationTask | null;
  contactName?: string | null;
  onComplete: (taskId: string, response: string) => Promise<void>;
  isSubmitting?: boolean;
}

export function EscalationTaskCompletionModal({
  open,
  onClose,
  task,
  contactName,
  onComplete,
  isSubmitting = false,
}: EscalationTaskCompletionModalProps) {
  const [response, setResponse] = useState('');

  const handleSubmit = async () => {
    if (!task) return;
    
    if (response.trim().length < 10) {
      toast.error('A resposta deve ter pelo menos 10 caracteres');
      return;
    }
    
    try {
      await onComplete(task.id, response);
      setResponse('');
      onClose();
    } catch (err) {
      toast.error('Erro ao concluir escalação');
    }
  };

  const handleClose = () => {
    setResponse('');
    onClose();
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-orange-500" />
            Responder Escalação
          </DialogTitle>
          <DialogDescription>
            Sua resposta será enviada automaticamente para o CS que escalou.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Badge indicando que é escalação */}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-800">
              🔺 Escalação CS
            </Badge>
            {task.priority === 'urgent' && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Urgente
              </Badge>
            )}
            {task.priority === 'high' && (
              <Badge variant="secondary" className="flex items-center gap-1 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                Alta
              </Badge>
            )}
          </div>

          {contactName && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Cliente:</span>
              <span className="text-sm font-medium">{contactName}</span>
            </div>
          )}


          {/* Pergunta original */}
          <div className="bg-muted p-4 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1 font-medium">Pergunta:</p>
            <p className="text-sm whitespace-pre-wrap">{task.description}</p>
          </div>

          {/* Campo de resposta */}
          <div className="space-y-2">
            <Label>Sua Resposta *</Label>
            <Textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Digite sua resposta detalhada para o CS..."
              rows={5}
              className={response.length > 0 && response.length < 10 ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              {response.length}/10 caracteres mínimos • Esta resposta ficará visível na sessão de atendimento
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting || response.trim().length < 10}
            className="bg-green-600 hover:bg-green-700"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            <CheckCircle className="h-4 w-4 mr-2" />
            Responder e Concluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
